/**
 * ScamSmartGuard — gates paid ScamSmart routes.
 *
 * Pattern: ScamSmart uses GUEST checkout (no login), so the existing cookie-based
 * <ProtectedRoute> doesn't apply. Instead we store the buyer's email in localStorage
 * after the Thank-You page sees it (either via bypass query param or via a backend
 * lookup against the Stripe session_id). On every paid-page mount, we re-verify
 * with the backend via /api/scamsmart/access-status to confirm the email still has
 * an active 'scamsmart-access' purchase record.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Lock, ArrowRight } from 'lucide-react';
import { API_URL } from '../config';

const API = `${API_URL}/api`;
const SS_EMAIL_KEY = 'ss_email';

export const getStoredScamSmartEmail = () => {
  try { return localStorage.getItem(SS_EMAIL_KEY) || ''; } catch { return ''; }
};
export const setStoredScamSmartEmail = (email) => {
  try { localStorage.setItem(SS_EMAIL_KEY, email); } catch {}
};

const ScamSmartGuard = ({ children }) => {
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, email: '', hasAccess: false });

  useEffect(() => {
    const email = getStoredScamSmartEmail();
    if (!email) {
      setState({ loading: false, email: '', hasAccess: false });
      return;
    }
    axios.get(`${API}/scamsmart/access-status`, { params: { email } })
      .then(({ data }) => setState({ loading: false, email, hasAccess: !!data.has_access }))
      .catch(() => setState({ loading: false, email, hasAccess: false }));
  }, []);

  if (state.loading) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        minHeight: '100vh', background: '#F7F9FB', color: '#0A1F44',
        fontFamily: "'Montserrat', sans-serif",
      }}>
        <Loader2 size={36} style={{ animation: 'ss-spin 1s linear infinite' }} />
        <style>{`@keyframes ss-spin { from { transform: rotate(0deg);} to { transform: rotate(360deg);} }`}</style>
      </div>
    );
  }

  if (!state.hasAccess) {
    return (
      <div className="ss-page" data-testid="ss-guard-locked">
        <div className="ss-test-wrap">
          <div className="ss-test-container" style={{ paddingTop: 60 }}>
            <div className="ss-preview-card" style={{ textAlign: 'center' }}>
              <Lock size={36} color="#16B1C4" strokeWidth={1.5} style={{ margin: '0 auto 16px' }} />
              <h1 style={{ color: '#fff', fontSize: 24, fontWeight: 800, margin: '0 0 8px' }}>
                ScamSmart is locked
              </h1>
              <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 14, margin: '0 0 22px' }}>
                Take the free Scam IQ test to preview, then unlock the full training for $14.99 AUD.
              </p>
              <button
                className="ss-btn-primary"
                onClick={() => navigate('/scamsmart')}
                style={{ width: '100%', justifyContent: 'center' }}
                data-testid="ss-guard-cta"
              >
                Open ScamSmart <ArrowRight size={18} />
              </button>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, marginTop: 16 }}>
                Already paid on another device? <Link to="/scamsmart#restore" style={{ color: '#16B1C4' }}>Restore access</Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return React.cloneElement(children, { ssEmail: state.email });
};

export default ScamSmartGuard;
